import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, MessageCircle, Sparkles, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import PageMeta from "../components/PageMeta";
import BrandLogo from "../components/BrandLogo";

/**
 * /thank — a simple thank-you page shown after form submission
 * or accessible directly for campaigns / email links.
 */
export default function Thank() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="thank-page">
      <PageMeta
        title="Thank You — Prime Wiki Services"
        description="Thanks for reaching out — a senior Wikipedia specialist will be in touch within two hours on business days."
        noindex
      />

      {/* Minimal top bar */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" data-testid="thank-logo"><BrandLogo /></Link>
        </div>
      </header>

      <main className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl flex-col items-center justify-center px-4 py-16 text-center sm:px-6 lg:px-8">
        <div className="grid h-20 w-20 place-items-center rounded-full bg-emerald-500 text-white shadow-[0_15px_40px_-15px_rgba(16,185,129,0.7)]">
          <CheckCircle2 className="h-10 w-10" />
        </div>

        <span className="mt-7 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          <Sparkles className="h-3.5 w-3.5" /> Received · We're on it
        </span>

        <h1 className="font-serif mt-6 text-4xl font-semibold leading-[1.05] text-[#0A2540] sm:text-5xl lg:text-6xl">
          Thank you — your request is in.
        </h1>

        <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
          A senior Wikipedia specialist has been assigned to your enquiry. We'll review your notability and reply from
          <span className="font-semibold text-[#0A2540]"> info@Primewikiservices.com</span> with next steps.
        </p>

        {/* What happens next */}
        <div className="mt-10 grid w-full gap-4 sm:grid-cols-3">
          {[
            { i: Clock, t: "Within 2 hours", d: "A specialist reads your enquiry — no bots, no auto-replies." },
            { i: CheckCircle2, t: "Free notability review", d: "You get a written verdict on your Wikipedia eligibility." },
            { i: MessageCircle, t: "Plain-English proposal", d: "Clear scope, clear fees, clear timeline — or a polite \"not yet\"." },
          ].map((s) => (
            <div key={s.t} className="rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-50 text-emerald-600"><s.i className="h-5 w-5" /></div>
              <div className="font-serif mt-3 text-base font-semibold text-[#0A2540]">{s.t}</div>
              <div className="mt-1 text-sm text-slate-600">{s.d}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/">
            <Button
              className="h-12 rounded-full px-6 text-sm font-semibold text-white shadow-[0_10px_24px_-10px_rgba(16,185,129,0.6)]"
              style={{ background: "linear-gradient(135deg, #0A2540 0%, #10B981 165%)" }}
              data-testid="thank-home-button"
            >
              Back to homepage <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <button
            type="button"
            className="chat h-12 rounded-full border border-slate-300 px-6 text-sm font-semibold text-[#0A2540] hover:border-slate-400"
            data-testid="thank-chat-button"
          >
            <MessageCircle className="mr-2 inline h-4 w-4" /> Chat with a specialist
          </button>
        </div>

        <p className="mt-10 text-xs text-slate-500">
          Reference: keep this tab open if you were sent here from a specific campaign — your confirmation email will follow shortly.
        </p>
      </main>
    </div>
  );
}
